import {
  faCircle,
  faCircleExclamation,
} from "@fortawesome/pro-regular-svg-icons";
import { useFormState } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { StepSidebar } from "src/cases/common/StepSidebar";
import { createStepErrorNavigation } from "src/cases/common/stepErrorNavigation";
import {
  CreateCaseFormValues,
  CreateCaseStep,
  CreateCaseStepId,
  useCurrentCreateCaseStep,
} from "src/cases/createCase/createCaseSteps";
import { Icon, IconVariant } from "src/design-system/Icon";

const { stepHasErrors } = createStepErrorNavigation<CreateCaseStepId>([
  { pathPrefix: "source", stepId: "context" },
  { pathPrefix: "comment", stepId: "context" },
  { pathPrefix: "file_category_files", stepId: "context" },
  { pathPrefix: "case_properties", stepId: "properties" },
  { pathPrefix: "status", stepId: "case-routing" },
  { pathPrefix: "sub_status", stepId: "case-routing" },
  { pathPrefix: "assignee_id", stepId: "case-routing" },
]);

type CreateCaseSidebarProps = {
  steps: CreateCaseStep[];
};

export const CreateCaseSidebar: React.FC<CreateCaseSidebarProps> = ({
  steps,
}) => {
  const [currentStep, setCurrentStep] = useCurrentCreateCaseStep(steps);
  const { errors } = useFormState<CreateCaseFormValues>();

  return (
    <StepSidebar
      ariaLabel="Create case form steps"
      currentStepId={currentStep.id}
      steps={steps}
    >
      {(step, index) => {
        const hasErrors = stepHasErrors(errors, step.id as CreateCaseStepId);

        return (
          <StepButton
            active={currentStep.id === step.id}
            hasErrors={hasErrors}
            stepNumber={index + 1}
            totalSteps={steps.length}
            onClick={() => setCurrentStep(step.id)}
          >
            {step.title}
          </StepButton>
        );
      }}
    </StepSidebar>
  );
};

const StepButton: React.FC<{
  active: boolean;
  hasErrors: boolean;
  onClick: () => void;
  children: string;
  stepNumber: number;
  totalSteps: number;
}> = ({ active, hasErrors, onClick, children, stepNumber, totalSteps }) => {
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onClick();
    }
  };

  const getIcon = () => {
    if (hasErrors) return faCircleExclamation;
    return faCircle;
  };

  const getIconVariant = (): IconVariant => {
    if (hasErrors) return "error";
    return "default";
  };

  return (
    <button
      aria-current={active ? "step" : undefined}
      aria-label={`Step ${stepNumber} of ${totalSteps}: ${children}${hasErrors ? " (has errors)" : ""}`}
      className={twMerge(
        "z-10 flex h-6 w-full items-center gap-x-2 rounded-lg px-2 transition-all duration-300",
        !active && "cursor-pointer hover:bg-gray-50",
        "font-inter-medium-13px",
      )}
      type="button"
      onClick={onClick}
      onKeyDown={handleKeyDown}
    >
      <Icon
        aria-hidden="true"
        icon={getIcon()}
        size="xs"
        variant={getIconVariant()}
      />
      {children}
    </button>
  );
};
